import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  Dashboard,
  DashboardWidget,
  WidgetTemplate,
  Page,
  WidgetLayoutUpdate,
} from '../types/widget';
import type { QueryFilter } from '../types/analytics';

interface DashboardsState {
  // Current page context
  currentPage: Page | null;

  // My Dashboards
  dashboards: Dashboard[];
  currentDashboard: Dashboard | null;
  dashboardsLoading: boolean;

  // Widgets
  widgets: DashboardWidget[];
  widgetsLoading: boolean;

  // Templates
  templates: WidgetTemplate[];
  templatesLoading: boolean;

  // UI State
  isTemplateLibraryOpen: boolean;
  isDashboardCreateModalOpen: boolean;
  selectedWidgetId: number | null;

  // Filter State
  dashboardFilters: QueryFilter[];

  // Error handling
  error: string | null;
}

interface DashboardsActions {
  // Page actions
  setCurrentPage: (page: Page | null) => void;

  // Dashboard actions
  setDashboards: (dashboards: Dashboard[]) => void;
  setCurrentDashboard: (dashboard: Dashboard | null) => void;
  addDashboard: (dashboard: Dashboard) => void;
  updateDashboard: (id: number, updates: Partial<Dashboard>) => void;
  removeDashboard: (id: number) => void;
  setDashboardsLoading: (loading: boolean) => void;

  // Widget actions
  setWidgets: (widgets: DashboardWidget[]) => void;
  addWidget: (widget: DashboardWidget) => void;
  updateWidget: (id: number, updates: Partial<DashboardWidget>) => void;
  removeWidget: (id: number) => void;
  updateWidgetLayouts: (layouts: WidgetLayoutUpdate[]) => void;
  setWidgetsLoading: (loading: boolean) => void;

  // Template actions
  setTemplates: (templates: WidgetTemplate[]) => void;
  setTemplatesLoading: (loading: boolean) => void;

  // UI actions
  setTemplateLibraryOpen: (open: boolean) => void;
  setDashboardCreateModalOpen: (open: boolean) => void;
  setSelectedWidgetId: (id: number | null) => void;

  // Filter actions
  setDashboardFilters: (filters: QueryFilter[]) => void;
  addDashboardFilter: (filter: QueryFilter) => void;
  removeDashboardFilter: (filter: QueryFilter) => void;
  clearDashboardFilters: () => void;

  // Error handling
  setError: (error: string | null) => void;
  clearError: () => void;

  // Reset
  reset: () => void;
}

type DashboardsStore = DashboardsState & DashboardsActions;

const initialState: DashboardsState = {
  currentPage: null,
  dashboards: [],
  currentDashboard: null,
  dashboardsLoading: false,
  widgets: [],
  widgetsLoading: false,
  templates: [],
  templatesLoading: false,
  isTemplateLibraryOpen: false,
  isDashboardCreateModalOpen: false,
  selectedWidgetId: null,
  dashboardFilters: [],
  error: null,
};

export const useDashboardsStore = create<DashboardsStore>()(
  devtools(
    (set) => ({
      ...initialState,

      // Page actions
      setCurrentPage: (page) => set({ currentPage: page }),

      // Dashboard actions
      setDashboards: (dashboards) => set({ dashboards }),

      setCurrentDashboard: (dashboard) => set({ currentDashboard: dashboard }),

      addDashboard: (dashboard) =>
        set((state) => ({
          dashboards: [...state.dashboards, dashboard],
        })),

      updateDashboard: (id, updates) =>
        set((state) => ({
          dashboards: state.dashboards.map((d) =>
            d.id === id ? { ...d, ...updates } : d
          ),
          currentDashboard:
            state.currentDashboard?.id === id
              ? { ...state.currentDashboard, ...updates }
              : state.currentDashboard,
        })),

      removeDashboard: (id) =>
        set((state) => ({
          dashboards: state.dashboards.filter((d) => d.id !== id),
          currentDashboard:
            state.currentDashboard?.id === id ? null : state.currentDashboard,
        })),

      setDashboardsLoading: (loading) => set({ dashboardsLoading: loading }),

      // Widget actions
      setWidgets: (widgets) =>
        set(() => ({
          widgets: widgets.reduce<DashboardWidget[]>((acc, widget) => {
            return acc.find((w) => w.id === widget.id) ? acc : [...acc, widget];
          }, []),
        })),

      addWidget: (widget) =>
        set((state) => ({
          widgets: [...state.widgets.filter((w) => w.id !== widget.id), widget],
        })),

      updateWidget: (id, updates) =>
        set((state) => ({
          widgets: state.widgets.map((w) =>
            String(w.id) === String(id) ? { ...w, ...updates } : w
          ),
        })),

      removeWidget: (id) =>
        set((state) => ({
          // Use String() to normalize comparison — API returns id as string, callers may pass number
          widgets: state.widgets.filter((w) => String(w.id) !== String(id)),
          selectedWidgetId:
            state.selectedWidgetId === id ? null : state.selectedWidgetId,
        })),

      updateWidgetLayouts: (layouts) =>
        set((state) => ({
          widgets: state.widgets.map((widget) => {
            const layout = layouts.find((l) => String(l.id) === String(widget.id));
            if (layout) {
              return {
                ...widget,
                position_x: layout.position_x,
                position_y: layout.position_y,
                width: layout.width,
                height: layout.height,
                sort_index: layout.sort_index,
              };
            }
            return widget;
          }),
        })),

      setWidgetsLoading: (loading) => set({ widgetsLoading: loading }),

      // Template actions
      setTemplates: (templates) => set({ templates }),
      setTemplatesLoading: (loading) => set({ templatesLoading: loading }),

      // UI actions
      setTemplateLibraryOpen: (open) => set({ isTemplateLibraryOpen: open }),
      setDashboardCreateModalOpen: (open) =>
        set({ isDashboardCreateModalOpen: open }),
      setSelectedWidgetId: (id) => set({ selectedWidgetId: id }),

      // Filter actions
      setDashboardFilters: (filters) => set({ dashboardFilters: filters }),

      addDashboardFilter: (filter) =>
        set((state) => ({
          dashboardFilters: [...state.dashboardFilters, filter],
        })),

      removeDashboardFilter: (filter) =>
        set((state) => ({
          dashboardFilters: state.dashboardFilters.filter(
            (f) =>
              !(
                f.field === filter.field &&
                f.operator === filter.operator &&
                JSON.stringify(f.value) === JSON.stringify(filter.value)
              )
          ),
        })),

      clearDashboardFilters: () => set({ dashboardFilters: [] }),

      // Error handling
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),

      // Reset
      reset: () => set(initialState),
    }),
    { name: 'dashboards-store' }
  )
);
